import { useEffect, useMemo, useRef, useState } from "react";
import { Check, Download, Link2, MessageCircle, MessageSquareText, Share2, Facebook } from "lucide-react";
import { createShare } from "../../lib/api";
import { cardHeadline, drawCard, familyNameError } from "../../lib/shareCard";

const btn = "inline-flex items-center justify-center gap-2 rounded-full border border-[#FDFBF7]/25 bg-[#FDFBF7]/10 px-4 py-2.5 text-sm font-semibold text-[#FDFBF7] transition-colors duration-200 hover:bg-[#FDFBF7] hover:text-[#2C221E]";

const shareText = (headline) => `${headline}! Woven arrives Christmas Eve 2026 — a family life app that helps couples show up for all of it. Join the waitlist:`;

const ShareButtons = ({ url, headline, canvasRef }) => {
  const [copied, setCopied] = useState(false);
  const text = shareText(headline);
  const native = typeof navigator !== "undefined" && !!navigator.share && window.matchMedia?.("(pointer: coarse)").matches;

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(url);
    } catch {
      const ta = Object.assign(document.createElement("textarea"), { value: url });
      document.body.appendChild(ta);
      ta.select();
      document.execCommand("copy");
      ta.remove();
    }
    setCopied(true);
    setTimeout(() => setCopied(false), 2500);
  };

  const nativeShare = async () => {
    const blob = await new Promise((r) => canvasRef.current.toBlob(r, "image/png"));
    const file = new File([blob], "woven-christmas-card.png", { type: "image/png" });
    const data = { title: headline, text, url };
    try {
      await navigator.share(navigator.canShare?.({ files: [file] }) ? { ...data, files: [file] } : data);
    } catch { /* user cancelled */ }
  };

  const enc = encodeURIComponent;
  return (
    <div className="mt-5 flex flex-wrap gap-2">
      {native && <button type="button" onClick={nativeShare} data-testid="share-native-button" className={btn}><Share2 className="h-4 w-4" /> Share</button>}
      <a href={`https://wa.me/?text=${enc(`${text} ${url}`)}`} target="_blank" rel="noopener noreferrer" data-testid="share-whatsapp-link" className={btn}><MessageCircle className="h-4 w-4" /> WhatsApp</a>
      <a href={`https://www.facebook.com/sharer/sharer.php?u=${enc(url)}`} target="_blank" rel="noopener noreferrer" data-testid="share-facebook-link" className={btn}><Facebook className="h-4 w-4" /> Facebook</a>
      <a href={`sms:?&body=${enc(`${text} ${url}`)}`} data-testid="share-sms-link" className={btn}><MessageSquareText className="h-4 w-4" /> Text message</a>
      <button type="button" onClick={copy} data-testid="share-copy-link-button" className={btn}>
        {copied ? <Check className="h-4 w-4" /> : <Link2 className="h-4 w-4" />} {copied ? "Link copied" : "Copy link"}
      </button>
    </div>
  );
};

export const ShareCard = ({ email }) => {
  const canvasRef = useRef(null);
  const [name, setName] = useState("");
  const [share, setShare] = useState(null);
  const error = familyNameError(name.trim());
  const validName = error ? "" : name.trim().replace(/\s+/g, " ");
  const headline = useMemo(() => cardHeadline(validName), [validName]);

  useEffect(() => { drawCard(canvasRef.current, headline); }, [headline]);
  useEffect(() => {
    const t = setTimeout(() => createShare(email, validName).then(({ data }) => setShare(data)).catch(() => {}), validName ? 600 : 0);
    return () => clearTimeout(t);
  }, [email, validName]);

  const download = () => {
    canvasRef.current.toBlob((blob) => {
      const a = Object.assign(document.createElement("a"), { href: URL.createObjectURL(blob), download: "woven-christmas-card.png" });
      a.click();
      setTimeout(() => URL.revokeObjectURL(a.href), 1000);
    }, "image/png");
  };

  return (
    <div data-testid="share-card-panel" className="mt-4 rounded-[1.5rem] border border-[#FDFBF7]/15 bg-[#FDFBF7]/10 p-5 sm:p-6 backdrop-blur-md fade-up">
      <p className="font-serif text-xl text-[#FDFBF7]">Share the joy</p>
      <p className="mt-1 text-sm text-[#F4EBDD]/80">Let your people know you're joining — they might want to join you.</p>
      <div className="mt-5 grid gap-5 sm:grid-cols-[minmax(0,200px)_1fr] items-start">
        <canvas ref={canvasRef} data-testid="share-card-canvas" aria-label={`${headline} — Arriving Christmas Eve 2026`} className="w-full max-w-[240px] aspect-square rounded-2xl shadow-[0_12px_30px_rgba(0,0,0,0.3)]" />
        <div className="min-w-0">
          <label htmlFor="family-name" className="block text-sm font-medium text-[#FDFBF7]">Family name <span className="text-[#F4EBDD]/60">(optional)</span></label>
          <input id="family-name" value={name} onChange={(e) => setName(e.target.value)} placeholder="The Smiths" autoComplete="off"
            aria-invalid={!!error} data-testid="share-family-name-input"
            className="mt-2 w-full rounded-full bg-[#FDFBF7] px-4 py-2.5 text-base text-[#2C221E] placeholder:text-[#8A7A72] outline-none" />
          {error && <p role="alert" data-testid="share-family-name-error" className="mt-2 text-sm font-medium text-[#FFB59A]">{error}</p>}
          <p data-testid="share-card-headline" className="sr-only">{headline}</p>
          <button type="button" onClick={download} data-testid="share-download-button" className="btn-primary mt-4 justify-center !px-5 !py-2.5 text-sm">
            <Download className="h-4 w-4" /> Download card
          </button>
        </div>
      </div>
      {share && <ShareButtons url={share.url} headline={headline} canvasRef={canvasRef} />}
    </div>
  );
};
